type CbType = (...args: any[]) => any;

export interface IDebouncedFunction extends CbType {
	cancel: () => void;
	flush: () => void;
}

/**
 * Замедленный вызов функции
 * @param {function} cb - Функция
 * @param {number} [timeout=300] - Время до вызова функции
 * @param {boolean} [immediate=false] - Срочный вызов
 *
 * Возвращает функцию с методами:
 *  - `cancel()` — отменяет запланированный вызов
 *  - `flush()` — выполняет запланированный вызов немедленно
 */
export default function (
	cb: CbType,
	timeout = 300,
	immediate = false
): IDebouncedFunction {
	let timer: ReturnType<typeof setTimeout> | null = null;
	let lastContext: unknown = null;
	let lastArgs: IArguments | null = null;

	const debounced = function (this: unknown) {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		lastContext = this;
		lastArgs = arguments;
		const callNow = immediate && !timer;

		if (timer) {
			clearTimeout(timer);
		}

		timer = setTimeout(function () {
			timer = null;

			if (!immediate && lastArgs) {
				// @ts-ignore
				cb.apply(lastContext, lastArgs);
			}
			lastContext = null;
			lastArgs = null;
		}, timeout);

		if (callNow) {
			// @ts-ignore
			cb.apply(lastContext, lastArgs);
		}
	} as IDebouncedFunction;

	debounced.cancel = () => {
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}
		lastContext = null;
		lastArgs = null;
	};

	debounced.flush = () => {
		if (timer && !immediate && lastArgs) {
			clearTimeout(timer);
			timer = null;
			// @ts-ignore
			cb.apply(lastContext, lastArgs);
		}
		lastContext = null;
		lastArgs = null;
	};

	return debounced;
}
